import React, { useMemo } from "react";

import { useSelector } from "react-redux";
import { Field, FormSection, getFormValues } from "redux-form";
import {
	ReduxControls,
	ErrorBanner
} from "rhombus-react";

import DynamicAssetSelect from "social/DynamicAssetSelect";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

import { getErrorMessagesByAdType } from "selectors/Social2";

import { isRequiredAssetField, prettifyMessages } from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { LINKEDIN_IMAGE_CONSTRAINTS } from "social/validators/linkedIn";

import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { imposiumCompositionWithoutProjectName } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";

const {
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_INTRO,
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_MB,
	IMAGE_ACCEPT,
	DIMENSIONS,
} = LINKEDIN_IMAGE_CONSTRAINTS;

const LINKEDIN_IMAGE_REQUIREMENTS = [
	{
		name: "File Size:",
		description: `${MAX_IMAGE_MB}MB Maximum`,
	},
	{
		name: "File Types:",
		description: IMAGE_ACCEPT,
	},
	{
		name: "Dimensions:",
		description: `Recommended: 1200 x 1200 pixels, greater than ${DIMENSIONS.minWidth} x ${DIMENSIONS.minHeight} pixels`,
	}
];

const imageTip = {
	direction: "top",
	description: `
		Recommended:<br />
		<br />
		Horizontal / Landscape<br />
		1.91:1 - 1200x628<br />
		<br />
		Square (recommended)<br />
		1200x1200<br />
		<br />
		Vertical<br />
		1:1.91 - 628x1200<br />
		2:3 - 600x900<br />
		4:5 - 720x900
	`,
	html: true,
};

export const LINKEDIN_IMAGE_FIELDS = {
	isDynamic,
	assetType,
	imposiumProject,
	imposiumComposition: imposiumCompositionWithoutProjectName,
	image: {
		name: "image",
		tip: imageTip,
		label: "Image",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: "product",
		requirements: LINKEDIN_IMAGE_REQUIREMENTS,
		accept: IMAGE_ACCEPT,
		required: true,
		className: "full-width",
	},
	name: {
		name: "name",
		label: "Creative Name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
		className: "full-width",
	},
	headline: {
		name: "headline",
		label: "Headline",
		placeholder: "Enter a short headline",
		component: DynamicInput,
		defaultDynVarName: "headline",
		tip: {
			description: "May get truncated after 70 characters",
			direction: "right",
		},
		charMax: MAX_LENGTH_HEADLINE,
		// showCharCount: true,
		required: true,
		className: "full-width",
	},
	intro: {
		name: "intro",
		label: "Intro",
		placeholder: "Tell people what your creative is about",
		component: DynamicInput,
		defaultDynVarName: "copy",
		tip: {
			description: "May get truncated after 150 characters",
			direction: "right",
		},
		charMax: MAX_LENGTH_INTRO,
		// showCharCount: true,
		required: true,
		className: "full-width",
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
		required: true,
		className: "full-width",
	},
};

const adType = SOCIAL_AD_TYPES.LINKEDIN_IMAGE;

export default function LinkedInImage(props) {
	const { navigationActions, libraryId, isViewOnly, showPristineErrors, doVersionsExist } = props;

	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const { assetType } = formValues[adType] || {};

	const errorMessages = useSelector(getErrorMessagesByAdType(adType, showPristineErrors));
	const prettyErrorMessages = useMemo(() => (
		prettifyMessages(errorMessages, LINKEDIN_IMAGE_FIELDS)
	), [errorMessages]);
	const hasErrors = prettyErrorMessages.length;

	let errorDisplay;
	if (hasErrors) {
		errorDisplay = (
			<ErrorBanner title="Sorry, please fix the errors below" messages={prettyErrorMessages} />
		);
	}

	const $fields = Object.values(LINKEDIN_IMAGE_FIELDS).map((fieldProps) => {
		const extraProps = {
		};

		if (fieldProps.name === "image") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
			extraProps.creativeAssetType = assetType;
			extraProps.required = isRequiredAssetField(assetType);
		}

		extraProps.disabled = isViewOnly;

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	<Field key={fieldProps.name} {...fieldProps} {...extraProps} />;
	});

	return (
		<div className="form-container">
			<FormSection name={adType}>
				{errorDisplay}
				<div className="fields">
					{$fields}
				</div>
			</FormSection>
		</div>
	);
}

LinkedInImage.displayName = "LinkedInImageForm";
